import { useState } from "react";

/**
 * Simple A/B Testing Hook (localStorage-based)
 *
 * Assigns a random variant on a user's first visit and persists the
 * choice in localStorage so the same variant is shown on repeat visits.
 *
 * Usage:
 *   const { variant, isVariant } = useABTest("hero-cta", ["book-call", "get-started"]);
 *
 *   if (isVariant("book-call")) {
 *     return <BookCallButton />;
 *   }
 *   return <GetStartedButton />;
 *
 * @param {string}   testName  Unique identifier for the test
 * @param {string[]} variants  Array of variant names (default: ["A", "B"])
 * @returns {{ variant: string, isVariant: (name: string) => boolean }}
 */
function useABTest(testName, variants = ["A", "B"]) {
  const storageKey = `ab_test_${testName}`;

  const [variant] = useState(() => {
    // Check for a previously-assigned variant
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored && variants.includes(stored)) {
        return stored;
      }
    } catch {
      // localStorage unavailable -- fall through to random assignment
    }

    // Assign a random variant and persist it
    const chosen = variants[Math.floor(Math.random() * variants.length)];

    try {
      localStorage.setItem(storageKey, chosen);
    } catch {
      // silently fail
    }

    return chosen;
  });

  /**
   * Check whether the current user is seeing a specific variant.
   * @param {string} name  Variant name to test against
   * @returns {boolean}
   */
  const isVariant = (name) => variant === name;

  return { variant, isVariant };
}

export default useABTest;
